import { Box, Button, Input, Text, TextProps } from '@betty/moonlight';
import { memo, useEffect, useRef, useState } from 'react';
import {
  ErrorsValuesType,
  UpdatedValues,
  PeriodInputProps,
} from './PeriodInput.types';
import { isEmpty, updateFieldValue, validateField } from './PeriodInput.utils';
import { useDispatch, useSelector } from '@betty/toolkit/state';
import {
  fetchLImits,
  resetUpdateLimitsDataState,
  selectLimitsDataState,
  updateLimits,
} from '../../state';
import { PeriodType } from '../../services/LimitsService.schemas';
import AdditionalInfo from '../AdditionalInfo/AdditionalInfo';
import { PERIOD_TYPES } from './PeriodInput.constants';
import { useDefaultLimitValue } from '../../../../hooks/useDefaultLimitValue';
import isEqual from 'lodash-es/isEqual';

function PeriodInput({ type }: PeriodInputProps) {
  const dispatch = useDispatch();

  const limitType = type === 'Time' ? 'Hours' : 'Amount';

  const defaultValues = useDefaultLimitValue({ type });
  const latestDefaultsRef = useRef<UpdatedValues[] | null>(null);

  const limitsDataState = useSelector(selectLimitsDataState);

  const [updatedValues, setUpdatedValues] = useState<UpdatedValues[]>([]);

  const [errors, setErrors] = useState<ErrorsValuesType>({});

  useEffect(() => {
    if (defaultValues?.length) {
      latestDefaultsRef.current = defaultValues;
    }
  }, [defaultValues]);

  useEffect(() => {
    if (!updatedValues.length && defaultValues?.length) {
      setUpdatedValues(defaultValues);
    }
  }, [defaultValues, updatedValues.length]);

  useEffect(() => {
    return () => {
      dispatch(resetUpdateLimitsDataState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!updatedValues) return;
    validateField(updatedValues, setErrors);
  }, [updatedValues]);

  const handleChange = (field: PeriodType) => (value: string) => {
    setUpdatedValues((prev) => updateFieldValue(prev, field, value, type));
  };

  const handleUpdateLimits = async () => {
    const filteredData = updatedValues?.filter((item) => item.amount !== 0);
    await dispatch(updateLimits(filteredData));
    await dispatch(fetchLImits());

    if (
      latestDefaultsRef.current &&
      !isEqual(latestDefaultsRef.current, updatedValues)
    ) {
      setUpdatedValues(latestDefaultsRef.current);
    }
  };

  return (
    <Box gap="space-200" marginTop="space-400">
      {PERIOD_TYPES.map((field) => (
        <Box key={field} gap="space-200" marginBottom="space-300">
          <Input
            value={
              updatedValues
                ?.find((item) => item.period === field)
                ?.amount?.toLocaleString('en-US') ?? ''
            }
            placeholderNS="responsibleGaming"
            placeholderKey={
              `${field.toLowerCase()}${limitType}` as TextProps['locKey']
            }
            onChangeText={handleChange(field)}
            keyboardType="numeric"
            enterKeyHint="go"
            error={errors[field] ?? undefined}
            maxLength={12}
          />

          {!errors[field] ? (
            <AdditionalInfo
              field={field}
              type={type}
              labelSymbol={type === 'Time' ? 'hours' : undefined}
            />
          ) : null}

          {errors[field] ? (
            <Text
              variant="caption"
              locNS="responsibleGaming"
              locKey={errors[field] as TextProps['locKey']}
              color="darkSurfaceError"
            />
          ) : null}
        </Box>
      ))}
      <Button
        variant="secondary"
        onPress={handleUpdateLimits}
        locNS="responsibleGaming"
        locKey="saveLimits"
        disabled={!isEmpty(errors)}
        loading={limitsDataState === 'pending'}
      />
    </Box>
  );
}

export default memo(PeriodInput);
